// AuthContext.js — self-hosted JWT auth (Firebase removed)
import { createContext, useState, useEffect, useContext, useCallback } from "react";
import { 
  signInWithGoogle, 
  signInWithEmail,
  registerWithEmailAndPassword,
  sendVerificationEmail,
  checkEmailVerified,
  resetPassword,
  logout,
  onAuthStateChanged,
  getStoredToken,
  getStoredUser,
  refreshAuthToken,
} from "./firebase";

// Device compatibility check
const checkDeviceCompatibility = () => {
  const issues = [];
  if (!navigator.cookieEnabled) issues.push("Cookies are disabled");
  if (!window.localStorage) issues.push("Local storage is not supported");
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
    issues.push("HTTPS required for authentication");
  }
  return issues;
};

// Create context
const AuthContext = createContext();

// AuthProvider to wrap the app
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [deviceIssues, setDeviceIssues] = useState([]);

  // Check device compatibility on mount
  useEffect(() => {
    const issues = checkDeviceCompatibility();
    setDeviceIssues(issues);
    if (issues.length > 0) console.warn("Device compatibility issues:", issues);
  }, []);

  // Listen for auth state changes (from our custom onAuthStateChanged)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged((authUser) => {
      if (authUser) {
        setUser(authUser);
        setToken(getStoredToken());
      } else {
        setUser(null);
        setToken(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Auto-refresh token before expiry (every 50 minutes)
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(async () => {
      const newToken = await refreshAuthToken();
      if (newToken) setToken(newToken);
    }, 50 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  // Handle sign in with Google (now shows error — no OAuth provider)
  const handleSignInWithGoogle = useCallback(async () => {
    setAuthError(null);
    try {
      await signInWithGoogle();
    } catch (error) {
      setAuthError(error.message);
      console.error("Sign in with Google failed:", error);
    }
  }, []);

  // Handle registration with email/password
  const handleRegister = useCallback(async (email, password, inviteCode, displayName) => {
    setAuthError(null);
    try {
      const newUser = await registerWithEmailAndPassword(email, password, displayName, inviteCode);
      setToken(getStoredToken());
      return { success: true, user: newUser, message: "Registration successful! You are now logged in." };
    } catch (error) {
      setAuthError(error.message);
      console.error("Registration failed:", error);
      return { success: false, error: error.message };
    }
  }, []);

  // Handle sign in with email/password
  const handleSignInWithEmail = useCallback(async (email, password) => {
    setAuthError(null);
    try {
      const loggedInUser = await signInWithEmail(email, password);
      setToken(getStoredToken());
      return { success: true, user: loggedInUser, emailVerified: true };
    } catch (error) {
      setAuthError(error.message);
      console.error("Sign in with email failed:", error);
      return { success: false, error: error.message };
    }
  }, []);

  // Handle password reset
  const handlePasswordReset = useCallback(async (email) => {
    setAuthError(null);
    try {
      await resetPassword(email);
      return { success: true, message: "Password reset email sent!" };
    } catch (error) {
      setAuthError(error.message);
      return { success: false, error: error.message };
    }
  }, []);

  // Handle sending verification email
  const handleSendVerificationEmail = useCallback(async () => {
    try {
      const result = await sendVerificationEmail();
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  // Reload user / check verification status
  const handleReloadUser = useCallback(async () => {
    try {
      const verified = await checkEmailVerified();
      if (verified && user && !user.emailVerified) {
        const updatedUser = { ...user, emailVerified: true };
        localStorage.setItem('auth_user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        return updatedUser;
      }
      const newToken = await refreshAuthToken();
      if (newToken) {
        setToken(newToken);
        return user;
      }
      return null;
    } catch (error) {
      console.error("Reload user failed:", error);
      return null;
    }
  }, [user]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    setAuthError(null);
    try {
      await logout();
      window.location.href = '/';
    } catch (error) {
      setAuthError(error.message);
      console.error("Logout failed:", error);
    }
  }, []);

  const value = {
    user,
    token,
    loading,
    error: authError,
    deviceIssues,
    isEmailVerified: user?.emailVerified ?? true,
    signInWithGoogle: handleSignInWithGoogle,
    signInWithEmail: handleSignInWithEmail,
    register: handleRegister,
    sendVerificationEmail: handleSendVerificationEmail,
    reloadUser: handleReloadUser,
    resetPassword: handlePasswordReset,
    logout: handleLogout,
    clearError: () => setAuthError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook for easy use in components
export const useAuth = () => useContext(AuthContext);
